import React, { useMemo, useState } from "react";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import {
  Cloud, CloudRain, Wind, Thermometer, Radio, MapPin,
  Satellite, Zap, Waves as WavesIcon, Gauge, Tornado, Maximize2, Snowflake, Eye,
  Droplets, Sun, Wind as Air, Flame, Compass, Activity, Share2,
} from "lucide-react";

// All Windy overlays we expose. Each tab gives a different live storm/weather view.
const LAYERS = [
  { id: "satellite",  labelKey: "layer_satellite",  overlay: "satellite",  product: "satellite", icon: Satellite,   accent: "#264653" },
  { id: "radar",      labelKey: "layer_radar",      overlay: "radar",      product: "radar",     icon: Radio,       accent: "#2A9D8F" },
  { id: "clouds",     labelKey: "layer_clouds",     overlay: "clouds",     product: "ecmwf",     icon: Cloud,       accent: "#5C757D" },
  { id: "rain",       labelKey: "layer_rain",       overlay: "rain",       product: "ecmwf",     icon: CloudRain,   accent: "#1B3B6F" },
  { id: "thunder",    labelKey: "layer_thunder",    overlay: "cape",       product: "ecmwf",     icon: Zap,         accent: "#F4A261" },
  { id: "wind",       labelKey: "layer_wind",       overlay: "wind",       product: "ecmwf",     icon: Wind,        accent: "#E76F51" },
  { id: "gust",       labelKey: "layer_gust",       overlay: "gust",       product: "ecmwf",     icon: Tornado,     accent: "#D62828" },
  { id: "hurricanes", labelKey: "layer_hurricane",  overlay: "wind",       product: "ecmwf",     icon: Tornado,     accent: "#7a1f1f", hurricanes: true },
  { id: "waves",      labelKey: "layer_waves",      overlay: "waves",      product: "gfsWaves",  icon: WavesIcon,   accent: "#1B3B6F" },
  { id: "swell",      labelKey: "layer_swell",      overlay: "swell1",     product: "gfsWaves",  icon: WavesIcon,   accent: "#2A9D8F" },
  { id: "swell2",     labelKey: "layer_swell2",     overlay: "swell2",     product: "gfsWaves",  icon: WavesIcon,   accent: "#3b8af7" },
  { id: "wavePeriod", labelKey: "layer_wave_period",overlay: "wavesPeriod",product: "gfsWaves",  icon: Activity,    accent: "#1B3B6F" },
  { id: "sst",        labelKey: "layer_sst",        overlay: "sst",        product: "ecmwfWaves",icon: Thermometer, accent: "#E76F51" },
  { id: "currents",   labelKey: "layer_currents",   overlay: "currents",   product: "cmems",     icon: Compass,     accent: "#3b8af7" },
  { id: "pressure",   labelKey: "layer_pressure",   overlay: "pressure",   product: "ecmwf",     icon: Gauge,       accent: "#8b2a8e" },
  { id: "temp",       labelKey: "layer_temp",       overlay: "temp",       product: "ecmwf",     icon: Thermometer, accent: "#F4A261" },
  { id: "humidity",   labelKey: "layer_humidity",   overlay: "rh",         product: "ecmwf",     icon: Droplets,    accent: "#2A9D8F" },
  { id: "dewpoint",   labelKey: "layer_dewpoint",   overlay: "dewpoint",   product: "ecmwf",     icon: Droplets,    accent: "#1B3B6F" },
  { id: "uv",         labelKey: "layer_uv",         overlay: "uvindex",    product: "ecmwf",     icon: Sun,         accent: "#F4A261" },
  { id: "airq",       labelKey: "layer_airq",       overlay: "no2",        product: "cams",      icon: Air,         accent: "#8b2a8e" },
  { id: "dust",       labelKey: "layer_dust",       overlay: "pm2p5",      product: "cams",      icon: Flame,       accent: "#a8552a" },
  { id: "drought",    labelKey: "layer_drought",    overlay: "moistureAnom40",product: "ecmwf",  icon: Flame,       accent: "#a8552a" },
  { id: "snow",       labelKey: "layer_snow",       overlay: "snowAccu",   product: "ecmwf",     icon: Snowflake,   accent: "#3b8af7" },
  { id: "visibility", labelKey: "layer_visibility", overlay: "visibility", product: "ecmwf",     icon: Eye,         accent: "#5C757D" },
];

function buildWindyUrl({ lat, lon, overlay, product, hurricanes }) {
  // We explicitly disable Windy's calendar (`calendar=now`) so the play timeline
  // bar + ▶ play / +/- buttons at the bottom of the map have room to render.
  // When `calendar=12` is on, Windy squeezes the play strip to almost nothing —
  // its play buttons become invisible. The Sunday/Monday forecast detail is
  // already covered by our in-app SurfReport (Semana tab) + WeatherCard, so
  // dropping Windy's calendar costs the user nothing.
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    detailLat: String(lat),
    detailLon: String(lon),
    zoom: "6",
    level: "surface",
    overlay,
    product,
    menu: "true",
    message: "true",
    marker: "true",
    calendar: "now",
    pressure: "true",
    type: "map",
    location: "coordinates",
    detail: "true",
    metricWind: "kmh",
    metricTemp: "°C",
    radarRange: "-1",
  });
  if (hurricanes) {
    params.set("hurricanes", "true");
    params.set("isolines", "pressure");
  }
  return `https://embed.windy.com/embed2.html?${params.toString()}`;
}

export default function WeatherRadar({ beach }) {
  const { t } = useI18n();
  // Read ?radar=<layerId> from the URL so shared links land on the right overlay.
  const initialLayer = React.useMemo(() => {
    if (typeof window === "undefined") return "satellite";
    const q = new URLSearchParams(window.location.search).get("radar");
    return q && LAYERS.find((l) => l.id === q) ? q : "satellite";
  }, []);
  const [activeLayer, setActiveLayer] = useState(initialLayer);
  const [fullscreen, setFullscreen] = useState(false);
  const [shared, setShared] = useState(false);
  const lat = beach?.lat ?? 11.2526;
  const lon = beach?.lon ?? -85.8708;

  // If the page was opened via a share link, scroll the radar into view once.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (!new URLSearchParams(window.location.search).get("radar")) return;
    const el = document.querySelector("[data-testid='weather-radar']");
    if (el) queueMicrotask(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, []);

  const layer = LAYERS.find((l) => l.id === activeLayer) || LAYERS[0];
  const src = useMemo(
    () => buildWindyUrl({ lat, lon, overlay: layer.overlay, product: layer.product, hurricanes: !!layer.hurricanes }),
    [lat, lon, layer]
  );
  const ActiveIcon = layer.icon;

  const handleShare = async () => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("radar", layer.id);
    url.hash = "weather";
    const shareUrl = url.toString();
    const layerName = t(layer.labelKey);
    const text = (t("share_radar_text") || "").replace("{layer}", layerName);
    try {
      if (navigator.share) {
        await navigator.share({ title: t("share_radar_title"), text, url: shareUrl });
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast(t("share_copied"));
      } else {
        // Final fallback — surface the URL via a prompt so the user can copy manually.
        window.prompt(t("share_copied"), shareUrl);
      }
      setShared(true);
      setTimeout(() => setShared(false), 1500);
    } catch (err) {
      // AbortError happens when the user dismisses the native share sheet — that's not a failure.
      if (err && err.name !== "AbortError") {
        toast(t("share_failed"));
      }
    }
  };

  // Lock body scroll while in fullscreen so the fixed overlay never appears
  // squeezed between page content (Windy's play bar gets cut otherwise).
  React.useEffect(() => {
    if (!fullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [fullscreen]);

  return (
    <section
      data-testid="weather-radar"
      className={`clay p-0 overflow-hidden ${fullscreen ? "fixed inset-0 z-[60] rounded-none flex flex-col" : "relative"}`}
      style={fullscreen ? { borderRadius: 0, background: "var(--sand)" } : {}}
    >
      {/* Header bar */}
      <div className="flex items-start justify-between gap-4 px-5 md:px-7 pt-5 md:pt-6 pb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center transition-colors shrink-0"
            style={{ background: `${layer.accent}1f`, color: layer.accent }}
          >
            <ActiveIcon className="w-5 h-5 md:w-6 md:h-6" />
          </div>
          <div className="min-w-0">
            <p className="eyebrow flex items-center gap-2">
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--teal)] animate-pulse" />
                LIVE
              </span>
              <span className="text-[color:var(--ink-2)]">{t("radar")}</span>
            </p>
            <h3
              data-testid="radar-active-layer-title"
              className="text-xl md:text-2xl font-semibold mt-0.5 truncate"
              style={{ fontFamily: "Outfit" }}
            >
              {t(layer.labelKey)}
            </h3>
            <p className="text-xs text-[color:var(--ink-2)] mt-0.5 truncate">
              <MapPin className="inline w-3 h-3 -mt-0.5 mr-0.5 text-[color:var(--coral)]" />
              {beach?.name || "Popoyo"} · {t("radar_source_windy")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="radar-share-btn"
            onClick={handleShare}
            className="pill px-3 py-1.5 text-xs font-medium bg-[color:var(--coral)] text-white hover:opacity-90 flex items-center gap-1.5"
            aria-label="Share this storm view"
          >
            <Share2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{shared ? t("share_copied") : t("share")}</span>
          </button>
          <button
            data-testid="radar-fullscreen-btn"
            onClick={() => setFullscreen((f) => !f)}
            className="pill px-3 py-1.5 text-xs font-medium bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)] flex items-center gap-1.5"
            aria-label="Toggle fullscreen radar"
          >
            <Maximize2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{fullscreen ? t("exit_fullscreen") : t("fullscreen")}</span>
          </button>
        </div>
      </div>

      {/* Layer pills — scrollable on mobile */}
      <div
        data-testid="radar-layers"
        className="px-5 md:px-7 pb-3 flex gap-1.5 overflow-x-auto thin-scroll snap-x snap-mandatory -mx-1 px-6"
      >
        {LAYERS.map((l) => {
          const Icon = l.icon;
          const active = l.id === activeLayer;
          return (
            <button
              key={l.id}
              data-testid={`radar-layer-${l.id}`}
              onClick={() => setActiveLayer(l.id)}
              className={`pill px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5 shrink-0 snap-start whitespace-nowrap ${
                active
                  ? "text-white shadow-sm"
                  : "bg-[color:var(--sand-2)] text-[color:var(--ink)] hover:bg-[color:var(--sand-2)]/70"
              }`}
              style={active ? { background: l.accent } : {}}
            >
              <Icon className="w-3.5 h-3.5" />
              {t(l.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Windy iframe — generous height on mobile so Windy can render its native play /
          timeline strip AND the Sunday/Monday calendar at the bottom without overlap.
          (Calendar takes ~180 px; if the iframe is too short, the calendar gets stacked
          on top of the play controls and the user can't see them.) */}
      <div
        className={`relative w-full bg-[color:var(--sand-2)] ${fullscreen ? "flex-1 min-h-0" : ""}`}
      >
        <div
          className="block sm:hidden h-full"
          style={fullscreen ? {} : { height: "calc(100dvh - 240px)", minHeight: 480 }}
        >
          <iframe
            key={src + "-m"}
            data-testid="radar-iframe"
            title="Live weather map"
            src={src}
            width="100%"
            height="100%"
            frameBorder="0"
            loading="lazy"
            allow="fullscreen; geolocation"
            style={{ display: "block", border: 0, width: "100%", height: "100%" }}
          />
        </div>
        <div
          className="hidden sm:block h-full"
          style={fullscreen ? {} : { height: 820 }}
        >
          <iframe
            key={src + "-d"}
            title="Live weather map"
            src={src}
            width="100%"
            height="100%"
            frameBorder="0"
            loading="lazy"
            allow="fullscreen; geolocation"
            style={{ display: "block", border: 0, width: "100%", height: "100%" }}
          />
        </div>
      </div>

      <div className="px-5 md:px-7 py-3 border-t border-[color:var(--hairline)] flex items-center justify-between gap-3 text-xs text-[color:var(--ink-2)]">
        <span className="hidden sm:inline">{t("radar_footer_hint")}</span>
        <span className="sm:hidden truncate">{t("radar_footer_hint_short")}</span>
        <a
          href={`https://www.windy.com/?${layer.hurricanes ? "hurricanes," : ""}${lat},${lon},6`}
          target="_blank"
          rel="noreferrer"
          className="text-[color:var(--navy)] font-medium hover:underline shrink-0"
          data-testid="open-windy-link"
        >
          {t("open_full_map")} →
        </a>
      </div>
    </section>
  );
}
